import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Trophy, MapPin, Clock, Zap, Users, Crown, Medal, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useI18n } from "../lib/i18n";
import { subscribeToPush, unsubscribeFromPush, getSubscriptionStatus, isPushSupported } from "../lib/push";

function formatDuration(sec) {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

function LeaderboardDialog({ comp, open, onClose }) {
  const [rows, setRows] = useState(null);
  const [live, setLive] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    if (!open || !comp) return;
    // build WS URL from backend URL
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/competitions/${comp.competition_id}/leaderboard`;

    let ws;
    let pingTimer;
    let reconnectTimer;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      ws = new WebSocket(wsUrl);
      ws.onopen = () => {
        setLive(true);
        // keep-alive ping
        pingTimer = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN) ws.send("ping");
        }, 25000);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "leaderboard") setRows(msg.rows);
        } catch {}
      };
      ws.onclose = () => {
        setLive(false);
        clearInterval(pingTimer);
        if (!cancelled) reconnectTimer = setTimeout(connect, 3000);
      };
      ws.onerror = () => {
        try { ws.close(); } catch {}
      };
    };

    connect();
    return () => {
      cancelled = true;
      clearInterval(pingTimer);
      clearTimeout(reconnectTimer);
      try { ws && ws.close(); } catch {}
    };
  }, [open, comp]);

  if (!comp) return null;
  const podiumIcons = [Crown, Medal, Medal];
  const podiumColors = ["text-gold-hover", "text-ink-700", "text-compass"];
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            {t("raceLeaderboard")}: {comp.name}
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-bold ${live ? "bg-forest text-white" : "bg-parchment-dark text-ink-500"}`} data-testid="live-indicator">
              <span className={`w-2 h-2 rounded-full ${live ? "bg-white animate-pulse" : "bg-ink-500"}`}/>
              {live ? "LIVE" : "OFFLINE"}
            </span>
          </DialogTitle>
          <DialogDescription className="text-ink-500">Aggiornamenti in tempo reale via WebSocket</DialogDescription>
        </DialogHeader>
        <div className="text-xs text-ink-500 mb-3">{rows?.length || 0} partecipanti</div>
        {!rows ? (
          <div className="text-center py-6 text-ink-500">Caricamento...</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-6 text-ink-500">Nessun partecipante ancora</div>
        ) : (
          <div className="space-y-2">
            {rows.map((r, i) => {
              const Icon = i < 3 ? podiumIcons[i] : null;
              return (
                <div key={r.user_id+i} className="flex items-center gap-3 p-3 rounded-2xl bg-parchment-dark" data-testid={`race-row-${i}`}>
                  <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center font-display font-bold text-ink-800">
                    {Icon ? <Icon className={podiumColors[i]} size={18}/> : i+1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-ink-800 truncate">{r.user_name}</div>
                    <div className="text-xs text-ink-500">
                      {r.status === "completed" ? `${t("completed")} · ${formatDuration(r.duration_sec)}` : t("inProgress")}
                    </div>
                  </div>
                  <div className="font-display font-extrabold text-gold-hover">{r.points_earned} pt</div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Race() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [showLB, setShowLB] = useState(null);
  const [pushStatus, setPushStatus] = useState({ supported: false, subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);

  const load = () => api.get("/competitions").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    (async () => {
      if (await isPushSupported()) {
        setPushStatus(await getSubscriptionStatus());
      }
    })();
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushStatus.subscribed) {
        await unsubscribeFromPush();
        toast.success("Notifiche disattivate");
      } else {
        await subscribeToPush();
        toast.success("Notifiche attivate! Sarai avvisato quando un avversario ti supera.");
      }
      setPushStatus(await getSubscriptionStatus());
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setPushBusy(false);
    }
  };

  const join = async (c) => {
    try {
      await api.post(`/competitions/${c.competition_id}/join`);
      toast.success("Iscritto alla gara!");
      navigate(`/play/${c.route_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 justify-between flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-compass flex items-center justify-center shadow-tactile">
            <Zap className="text-white" size={26}/>
          </div>
          <div>
            <h1 className="font-display font-extrabold text-3xl text-ink-900">{t("race")}</h1>
            <p className="text-ink-500 text-sm">Sfida gli altri esploratori per il tempo migliore</p>
          </div>
        </div>
        {pushStatus.supported && (
          <button
            onClick={togglePush}
            disabled={pushBusy}
            className={`rounded-2xl px-4 py-2 font-bold text-sm flex items-center gap-2 border ${
              pushStatus.subscribed
                ? "bg-forest text-white border-forest"
                : "bg-white text-ink-800 border-outline hover:bg-gold-light"
            }`}
            data-testid="push-toggle-btn"
          >
            {pushStatus.subscribed ? <Bell size={16}/> : <BellOff size={16}/>}
            {pushStatus.subscribed ? "Notifiche ON" : "Attiva notifiche"}
          </button>
        )}
      </div>

      {!items ? (
        <Card className="card-treasure p-8 text-center text-ink-500">Caricamento...</Card>
      ) : items.length === 0 ? (
        <Card className="card-treasure p-8 text-center text-ink-500">
          Nessuna gara attiva al momento. Torna più tardi!
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((c) => (
            <Card key={c.competition_id} className="card-treasure overflow-hidden p-0" data-testid={`race-card-${c.competition_id}`}>
              <div className="relative h-40">
                {c.route?.image_url && (
                  <img src={c.route.image_url} alt="" className="absolute inset-0 w-full h-full object-cover"/>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
                  <Badge className="bg-compass text-white border-0 rounded-full font-bold">
                    <Zap size={12} className="mr-1"/> {t("raceMode")}
                  </Badge>
                  {c.type === "family" && (
                    <Badge className="bg-ocean text-white border-0 rounded-full font-bold">
                      <Users size={12} className="mr-1"/> Family
                    </Badge>
                  )}
                </div>
                <div className="absolute bottom-3 left-3 right-3">
                  <h3 className="font-display font-bold text-xl text-white">{c.name}</h3>
                  <div className="text-white/80 text-sm">{c.route?.title}</div>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <div className="flex items-center gap-4 text-xs text-ink-500 font-bold flex-wrap">
                  {c.route?.city && <span className="flex items-center gap-1"><MapPin size={14}/>{c.route.city}</span>}
                  {c.route?.duration_min && <span className="flex items-center gap-1"><Clock size={14}/>{c.route.duration_min} {t("minutes")}</span>}
                  <span className="flex items-center gap-1"><Users size={14}/>{c.participants_count}</span>
                </div>
                <div className="p-3 rounded-2xl bg-gold-light border border-gold/40 text-sm text-ink-800">
                  <strong>{t("timeBonus")}:</strong> +{c.time_bonus_pct}% se finisci entro {c.time_bonus_minutes} {t("minutes")}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => join(c)}
                    disabled={c.already_joined}
                    className="btn-tactile flex-1 disabled:opacity-50"
                    data-testid={`race-join-${c.competition_id}`}
                  >
                    {c.already_joined ? t("alreadyJoined") : t("joinRace")}
                  </button>
                  <button
                    onClick={() => setShowLB(c)}
                    className="px-5 py-4 rounded-2xl bg-white border border-outline font-bold text-ink-800"
                    data-testid={`race-lb-${c.competition_id}`}
                  >
                    <Trophy size={18}/>
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <LeaderboardDialog comp={showLB} open={!!showLB} onClose={() => setShowLB(null)} />
    </div>
  );
}
